// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { DownloadOutlined, UndoOutlined } from '@ant-design/icons';
import { Button, Input, Modal, Select } from '@imprivata-cloud/components';
import { useConsentLocationSave, useDeleteDocument } from '@imprivata-cloud/core';
import { QueryKey } from '@portal/constants';
import { useQuery } from '@tanstack/react-query';
import type { FormProps } from 'antd';
import { Form, Upload } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';
import type { UploadChangeParam } from 'antd/es/upload';
import type { UploadFile } from 'antd/lib';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getCustomerAgreementInfo } from '../../api/portalServices';
import { type CustomerAgreementInfoGetResponse, DocumentPurpose } from '../../api/types';
import QuestionImg from '../../assets/icons/circle-question-mark.svg?react';
import { ErrorCode } from '../../errorHandler/constants';
import { useErrorContext } from '../../errorHandler/context/ErrorContext';
import { type ApiError, AppError } from '../../errorHandler/errors';
import useLegalFilesSave from '../../setup/hooks/useLegalFilesSave';
import './LegalFileUpload.less';
enum FieldNames {
  CUSTOMER_CONSENT = 'consentFile',
  CUSTOMER_NOTICE = 'noticeFile',
  REGIONS = 'regions',
}
interface FormValues {
  [FieldNames.CUSTOMER_NOTICE]: File;
  [FieldNames.CUSTOMER_CONSENT]: File;
  [FieldNames.REGIONS]: string[];
}

type FileFieldName = FieldNames.CUSTOMER_CONSENT | FieldNames.CUSTOMER_NOTICE;

interface FileField {
  purpose: DocumentPurpose;
  typeLabel: string;
  savedFiles: UploadFile[];
  setFileListOverride: (files: UploadFile[]) => void;
}

/** `confirmOpen` outlives the request so the modal keeps its copy while it closes. */
interface DeleteRequest {
  field: FileFieldName;
  confirmOpen: boolean;
}

const TRANSLATION_ROOT = 'portal.appearance';

export const LegalFileUpload = () => {
  const { t } = useTranslation();
  const { emitError, clearErrorNotifications } = useErrorContext();

  const [form] = Form.useForm<FormValues>();
  const [canSubmit, setCanSubmit] = useState(false);
  const [consentFileListOverride, setConsentFileListOverride] = useState<UploadFile[] | undefined>(
    undefined,
  );
  const [noticeFileListOverride, setNoticeFileListOverride] = useState<UploadFile[] | undefined>(
    undefined,
  );
  const [deleteRequest, setDeleteRequest] = useState<DeleteRequest | undefined>(undefined);
  const {
    data: documentsInfo,
    refetch,
    isPending,
    isError,
  } = useQuery<CustomerAgreementInfoGetResponse>({
    queryKey: [QueryKey.LEGAL_DOCUMENTS],
    queryFn: getCustomerAgreementInfo,
  });
  const legalFileSave = useLegalFilesSave();
  const { mutateAsync: consentLocationSave } = useConsentLocationSave();
  const { mutateAsync: deleteDocumentMutation } = useDeleteDocument();

  const {
    consentDocumentList,
    noticeDocumentList,
    fetchedConsentLocations,
    residenceLocationsList,
  } = useMemo(() => {
    const consentDoc: UploadFile[] = [];
    const noticeDoc: UploadFile[] = [];
    const regions =
      documentsInfo?.customerMandatoryResidenceLocationList.map(location => location.shortName) ??
      [];

    if (documentsInfo) {
      for (const document of documentsInfo.customerAgreementInfoList) {
        const file: UploadFile = {
          uid: '1',
          name: document.documentFilename,
          status: 'done',
          url: document.readUrl,
        };
        if (document.documentPurpose === DocumentPurpose.CONSENT) {
          consentDoc.push(file);
        } else if (document.documentPurpose === DocumentPurpose.NOTICE) {
          noticeDoc.push(file);
        }
      }
    }

    const locationList: DefaultOptionType[] =
      documentsInfo?.residenceLocationList.map(location => ({
        value: location.shortName,
        label: location.displayName,
        titleLabel: location.fullName,
      })) ?? [];

    return {
      consentDocumentList: consentDoc,
      noticeDocumentList: noticeDoc,
      fetchedConsentLocations: regions,
      residenceLocationsList: locationList,
    };
  }, [documentsInfo]);

  const fileFields: Record<FileFieldName, FileField> = {
    [FieldNames.CUSTOMER_CONSENT]: {
      purpose: DocumentPurpose.CONSENT,
      typeLabel: t(`${TRANSLATION_ROOT}.org-consent-document`),
      savedFiles: consentDocumentList,
      setFileListOverride: setConsentFileListOverride,
    },
    [FieldNames.CUSTOMER_NOTICE]: {
      purpose: DocumentPurpose.NOTICE,
      typeLabel: t(`${TRANSLATION_ROOT}.org-notice-document`),
      savedFiles: noticeDocumentList,
      setFileListOverride: setNoticeFileListOverride,
    },
  };

  const resetFormToServerSnapshot = async (snapshot?: CustomerAgreementInfoGetResponse) => {
    const regions =
      snapshot?.customerMandatoryResidenceLocationList.map(location => location.shortName) ?? [];

    form.resetFields();
    form.setFieldValue(FieldNames.REGIONS, regions);
    // Drop the overrides so both inputs read from the snapshot again; a caller that
    // wants a different display (`undo`) sets its override after this resolves.
    setConsentFileListOverride(undefined);
    setNoticeFileListOverride(undefined);
    await form.validateFields();
    setCanSubmit(false);
  };

  const handleSubmitButtonState = async () => {
    try {
      await form.validateFields();
      setCanSubmit(formHasChanged());
    } catch (error) {
      console.log('Validation failed: ', error);
      const hasErrors = form.getFieldsError().some(({ errors }) => errors.length > 0);
      setCanSubmit(!hasErrors && formHasChanged());
    }
  };

  const formHasChanged = () => {
    const consentHasChange = !!form.getFieldValue(FieldNames.CUSTOMER_CONSENT);
    const noticeHasChange = !!form.getFieldValue(FieldNames.CUSTOMER_NOTICE);
    const selectHasChange =
      form.getFieldValue(FieldNames.REGIONS)?.toLocaleString() !==
      fetchedConsentLocations?.toLocaleString();
    return consentHasChange || noticeHasChange || selectHasChange;
  };

  const onFinish: FormProps<FormValues>['onFinish'] = async (formValues: FormValues) => {
    try {
      clearErrorNotifications();
      await legalFileSave.mutateAsync({
        consentFile: formValues[FieldNames.CUSTOMER_CONSENT],
        noticeFile: formValues[FieldNames.CUSTOMER_NOTICE],
      });
      await consentLocationSave({ locations: formValues[FieldNames.REGIONS] });
      const { data } = await refetch();
      await resetFormToServerSnapshot(data);
    } catch (e) {
      emitError(new AppError(e as ApiError));
    }
  };

  function getFileValueFromEvent(e: UploadChangeParam) {
    if (e?.fileList?.length > 0) {
      return e.fileList.pop()?.originFileObj;
    }
    return e?.file?.originFileObj;
  }

  const setRegion = (regions: string[]) => {
    form.setFieldValue(FieldNames.REGIONS, regions.length === 0 ? undefined : regions);
    handleSubmitButtonState();
  };

  const validateFileExtension = (file: File, accept: string | undefined) => {
    if (!accept) {
      return true;
    }

    const fileName = file.name.toLowerCase();
    const fileType = file.type.toLowerCase();

    const isValid = accept
      .toLowerCase()
      .split(',')
      .map(item => item.trim())
      .some(acceptItem => {
        // (e.g.) .html - .json - .xml
        if (acceptItem.startsWith('.')) {
          return fileName.endsWith(acceptItem);
        }
        // (e.g.) audio/* - image/* - video/*
        if (acceptItem.endsWith('/*')) {
          return fileType.startsWith(acceptItem.replace('/*', ''));
        }
        return fileType === acceptItem; // (e.g.) image/png
      });
    if (!isValid) {
      emitError(
        AppError.fromMessage(ErrorCode.INVALID_LEGAL_DOCUMENT, ErrorCode.INVALID_LEGAL_DOCUMENT),
      );
      return Upload.LIST_IGNORE;
    }
    return isValid;
  };

  const handleFileChange = (fieldName: FieldNames) => {
    if (fieldName === FieldNames.CUSTOMER_CONSENT && consentFileListOverride !== undefined) {
      setConsentFileListOverride(undefined);
    }
    if (fieldName === FieldNames.CUSTOMER_NOTICE && noticeFileListOverride !== undefined) {
      setNoticeFileListOverride(undefined);
    }
  };

  const undo = async () => {
    const consentChanged = !!form.getFieldValue(FieldNames.CUSTOMER_CONSENT);
    const noticeChanged = !!form.getFieldValue(FieldNames.CUSTOMER_NOTICE);

    const { data } = await refetch();
    await resetFormToServerSnapshot(data ?? documentsInfo);

    if (consentChanged) {
      setConsentFileListOverride([]);
    }
    if (noticeChanged) {
      setNoticeFileListOverride([]);
    }
  };

  /**
   * The name the field should be displaying: a pending upload wins over the saved
   * document. A fresh array reference is what makes `Input.File` re-read the list.
   */
  const currentFileDisplay = (field: FileFieldName): UploadFile[] => {
    const pendingUpload: File | undefined = form.getFieldValue(field);
    return pendingUpload
      ? [{ uid: pendingUpload.name, name: pendingUpload.name, status: 'done' }]
      : [...fileFields[field].savedFiles];
  };

  const deleteDocument = async (field: FileFieldName) => {
    const { purpose, savedFiles } = fileFields[field];

    //Delete only locally from component.
    if (savedFiles.length === 0) {
      form.setFieldValue(field, undefined);
      handleSubmitButtonState();
      return;
    }

    //Delete using API.
    try {
      await deleteDocumentMutation(purpose);
      const { data } = await refetch();
      await resetFormToServerSnapshot(data);
    } catch (e) {
      // The display was cleared on confirm, but the document is still there.
      fileFields[field].setFileListOverride(currentFileDisplay(field));
      emitError(new AppError(e as ApiError));
    }
  };

  const requestDelete = async (field: FileFieldName) => {
    if (fileFields[field].savedFiles.length === 0) {
      await deleteDocument(field);
      return;
    }
    // `Input.File` blanks its own input the moment its remove icon is clicked, so the
    // name goes straight back and is only cleared once the admin confirms.
    fileFields[field].setFileListOverride(currentFileDisplay(field));
    setDeleteRequest({ field, confirmOpen: true });
  };

  const confirmDelete = async (field: FileFieldName) => {
    setDeleteRequest({ field, confirmOpen: false });
    fileFields[field].setFileListOverride([]);
    await deleteDocument(field);
  };

  const dismissDelete = ({ field }: DeleteRequest) => {
    setDeleteRequest({ field, confirmOpen: false });
  };

  if (isPending) {
    return <div className="legal-file-form">{t('common.loading')}</div>;
  }

  if (isError || !documentsInfo) {
    return <div className="legal-file-form">{t('common.error')}</div>;
  }

  const isFormDisabled = legalFileSave.isPending || !documentsInfo.customAgreementEnabled;

  return (
    <>
      <Form
        form={form}
        aria-label="legal-file-form-section"
        className="legal-file-form"
        onFinish={onFinish}
        onValuesChange={handleSubmitButtonState}
        layout="vertical"
      >
        <h2 className="section-title">{t(`${TRANSLATION_ROOT}.org-consent-title`)}</h2>
        <p>{t(`${TRANSLATION_ROOT}.org-consent-subtitle`)}</p>
        <a href="/exampleFiles/Empty_biometric_document.html" download>
          <Button type="default" icon={<DownloadOutlined className="download-icon" />} size="small">
            {t(`${TRANSLATION_ROOT}.org-download-sample-file`)}
          </Button>
        </a>

        <div className="field" aria-description="customer-consent-file">
          <label htmlFor="customer-consent-file-input">
            {t(`${TRANSLATION_ROOT}.org-customer-consent-file`)}
          </label>
          <Input.File
            id="customer-consent-file-input"
            btnText={t(`${TRANSLATION_ROOT}.org-upload-file-text`)}
            accept=".html"
            customRequest={({ onSuccess }) => onSuccess?.('ok')}
            maxCount={1}
            fieldName={FieldNames.CUSTOMER_CONSENT}
            valuePropName="file"
            getValueFromEvent={getFileValueFromEvent}
            beforeUpload={file => validateFileExtension(file, '.html')}
            fileList={consentFileListOverride ?? consentDocumentList}
            showUploadList={false}
            onChange={() => handleFileChange(FieldNames.CUSTOMER_CONSENT)}
            onRemove={() => requestDelete(FieldNames.CUSTOMER_CONSENT)}
            disabled={isFormDisabled}
          />
          <span className="caption">{t(`${TRANSLATION_ROOT}.org-legal-file-type`)}</span>
        </div>

        <div className="field" aria-description="customer-residence">
          <Form.Item
            name={FieldNames.REGIONS}
            dependencies={[FieldNames.CUSTOMER_CONSENT]}
            initialValue={fetchedConsentLocations}
            label={t(`${TRANSLATION_ROOT}.org-customer-residence`)}
          >
            {/*
            Note: admin can save consent doc and location individually.
            It is like a resumable feature for admin. It only goes into
            effect for end-user when both are present.
          */}
            <Select
              mode="multiple"
              allowClear
              options={residenceLocationsList}
              onChange={setRegion}
              disabled={isFormDisabled}
            />
          </Form.Item>
        </div>

        <div className="field" aria-description="customer-notice-file">
          <label htmlFor="customer-notice-file-input">
            {t(`${TRANSLATION_ROOT}.org-customer-notice-file`)}
          </label>
          <Input.File
            id="customer-notice-file-input"
            btnText={t(`${TRANSLATION_ROOT}.org-upload-file-text`)}
            accept=".html"
            customRequest={({ onSuccess }) => onSuccess?.('ok')}
            maxCount={1}
            fieldName={FieldNames.CUSTOMER_NOTICE}
            valuePropName="file"
            beforeUpload={file => validateFileExtension(file, '.html')}
            getValueFromEvent={getFileValueFromEvent}
            fileList={noticeFileListOverride ?? noticeDocumentList}
            showUploadList={false}
            onChange={() => handleFileChange(FieldNames.CUSTOMER_NOTICE)}
            onRemove={() => requestDelete(FieldNames.CUSTOMER_NOTICE)}
            disabled={isFormDisabled}
          />
          <span className="caption">{t(`${TRANSLATION_ROOT}.org-legal-file-type`)}</span>
        </div>

        <div className="btns-container">
          <Button
            type="text"
            className="text-button"
            onClick={() => undo()}
            disabled={!canSubmit || isFormDisabled}
            icon={<UndoOutlined className="undo-icon" />}
          >
            {t('common.undo')}
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={legalFileSave.isPending}
            disabled={isFormDisabled || !canSubmit}
          >
            {t('common.save')}
          </Button>
        </div>
      </Form>

      {deleteRequest && (
        <Modal
          open={deleteRequest.confirmOpen}
          className="delete-document-modal"
          width={430}
          closable={false}
          onCancel={() => dismissDelete(deleteRequest)}
          footer={
            <>
              <Button onClick={() => confirmDelete(deleteRequest.field)}>
                {t('common.remove')}
              </Button>
              <Button type="primary" onClick={() => dismissDelete(deleteRequest)}>
                {t('common.do-not-remove')}
              </Button>
            </>
          }
        >
          <QuestionImg className="confirm-icon" />
          <h3 className="confirm-title">
            {t(`${TRANSLATION_ROOT}.org-delete-legal-file-title`, {
              documentType: fileFields[deleteRequest.field].typeLabel,
            })}
          </h3>
          <p className="confirm-message">
            {t(`${TRANSLATION_ROOT}.org-delete-legal-file-subtitle`, {
              documentType: fileFields[deleteRequest.field].typeLabel,
            })}
          </p>
        </Modal>
      )}
    </>
  );
};
